import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Icon from '../components/dashboard/DashboardIcons';
import { getVisibleNavigation, getRouteTitle } from '../config/organizationNavigation';
import { getOrganizationType, getRoleLabel } from '../config/organization';
import { NotificationBell } from '../NotificationBanner';
import { GlobalSearchTrigger } from '../components/dashboard/GlobalSearch';
import HeaderHelpMenu from '../components/layout/HeaderHelpMenu';
import { SeasonSwitcher } from '../components/layout/SeasonSwitcher';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const isActive = (pathname, href) => pathname === href || pathname.startsWith(href + '/');

const SidebarLink = ({ item, collapsed, active, onNavigate }) => (
  <Link
    to={item.href}
    onClick={onNavigate}
    data-testid={`sidebar-link-${item.href.replace('/', '')}`}
    title={collapsed ? item.name : undefined}
    className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors duration-150 ${
      active ? 'bg-teal-500/15 text-teal-300 font-semibold' : 'text-slate-300 hover:bg-white/5 hover:text-white'
    } ${collapsed ? 'justify-center px-2' : ''}`}
  >
    <Icon name={item.icon} className="w-5 h-5 shrink-0" />
    {!collapsed && <span className="flex-1 truncate">{item.name}</span>}
    {!collapsed && item.soon && (
      <span className="shrink-0 rounded-full bg-orange-500/15 px-1.5 py-0.5 text-[10px] font-bold text-orange-400">قريباً</span>
    )}
  </Link>
);

const loadOpenGroups = () => {
  try {
    const raw = localStorage.getItem('orgNavOpenGroups');
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

const saveOpenGroups = (set) => {
  try { localStorage.setItem('orgNavOpenGroups', JSON.stringify([...set])); } catch { /* noop */ }
};

const SidebarContent = ({ collapsed, club, orgType, user, currentPath, onNavigate, logoUrl }) => {
  const navigation = getVisibleNavigation(user);
  const activeGroupKey = navigation.find((g) => g.items.some((i) => isActive(currentPath, i.href)))?.key || null;
  const [openGroups, setOpenGroups] = useState(() => {
    const saved = loadOpenGroups();
    if (saved) return new Set(saved);
    return new Set(activeGroupKey ? [activeGroupKey] : []);
  });

  // فتح مجموعة الصفحة الحالية تلقائياً دون إغلاق أي مجموعة أخرى مفتوحة
  useEffect(() => {
    if (activeGroupKey && !openGroups.has(activeGroupKey)) {
      setOpenGroups((prev) => {
        const next = new Set(prev);
        next.add(activeGroupKey);
        saveOpenGroups(next);
        return next;
      });
    }
    // eslint-disable-next-line
  }, [activeGroupKey]);

  const toggleGroup = (key) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      saveOpenGroups(next);
      return next;
    });
  };

  return (
    <div className="flex h-full flex-col">
      <div className={`flex items-center gap-3 border-b border-white/10 px-4 py-4 ${collapsed ? 'justify-center px-2' : ''}`}>
        {logoUrl ? (
          <img src={logoUrl} alt="شعار المنشأة" className="h-10 w-10 shrink-0 rounded-lg object-cover bg-white/10" onError={(e) => { e.target.style.display = 'none'; }} />
        ) : (
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-teal-500/20 text-teal-300">
            <Icon name="flag" className="w-5 h-5" />
          </div>
        )}
        {!collapsed && (
          <div className="min-w-0">
            <div className="truncate text-sm font-bold text-white" data-testid="sidebar-org-name">{club?.name || club?.club_name || 'المنشأة'}</div>
            <div className="text-xs text-slate-400">{orgType.label}</div>
          </div>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto px-3 pt-4 pb-16 space-y-1" data-testid="sidebar-nav">
        {navigation.map((group) => {
          if (group.items.length === 1) {
            const item = group.items[0];
            return (
              <div key={group.key} className="mt-3">
                <SidebarLink item={item} collapsed={collapsed} active={isActive(currentPath, item.href)} onNavigate={onNavigate} />
              </div>
            );
          }
          const isOpen = collapsed || openGroups.has(group.key);
          return (
            <div key={group.key}>
              {!collapsed && (
                <button
                  onClick={() => toggleGroup(group.key)}
                  data-testid={`sidebar-group-${group.key}`}
                  title={group.group}
                  className="mt-3 flex w-full items-center justify-between px-3 text-[11px] font-semibold uppercase tracking-wide text-slate-400 hover:text-slate-200 transition-colors"
                >
                  <span>{group.group}</span>
                  <Icon name="chevronDown" className={`w-3 h-3 transition-transform ${isOpen ? '' : '-rotate-90'}`} />
                </button>
              )}
              {isOpen && (
                <div className="mt-1 space-y-0.5">
                  {group.items.map((item) => (
                    <SidebarLink key={item.href} item={item} collapsed={collapsed} active={isActive(currentPath, item.href)} onNavigate={onNavigate} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </div>
  );
};

const TRIAL_CONTACT_INFO = '📞 تواصل مع فريق تسيار للترقية:\n\n📧 الإيميل: admin@platform.sa\n📱 الهاتف: +966 50 123 4567';

const TrialBanner = ({ status }) => {
  if (!status?.applicable || status.is_expired || status.days_remaining > 7) return null;
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-orange-200 bg-orange-50 px-4 py-2.5 sm:px-6" data-testid="trial-banner">
      <span className="text-sm font-semibold text-orange-800">
        تنتهي فترتك التجريبية بعد <span data-testid="trial-days-remaining">{status.days_remaining}</span> {status.days_remaining === 1 ? 'يوم' : 'أيام'} — رقِّ باقتك لتستمر بدون انقطاع
      </span>
      <button onClick={() => alert(TRIAL_CONTACT_INFO)} data-testid="trial-contact-btn" className="rounded-lg bg-orange-600 px-4 py-1.5 text-xs font-bold text-white hover:bg-orange-700 transition-colors">
        تواصل للترقية
      </button>
    </div>
  );
};

const TrialExpiredScreen = ({ onLogout }) => (
  <div className="flex min-h-[70vh] flex-col items-center justify-center px-6 text-center" data-testid="trial-expired-screen">
    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100 text-red-600">
      <Icon name="lock" className="w-8 h-8" />
    </div>
    <h2 className="mt-6 text-2xl font-bold text-slate-800">انتهت فترتك التجريبية</h2>
    <p className="mt-2 max-w-md text-slate-500">
      انتهت الفترة التجريبية المجانية لمنشأتك (30 يوماً). تواصل معنا لترقية باقتك والاستمرار في استخدام لوحة التحكم دون انقطاع.
    </p>
    <div className="mt-6 flex gap-3">
      <button onClick={() => alert(TRIAL_CONTACT_INFO)} data-testid="trial-expired-contact-btn" className="rounded-xl bg-teal-600 px-6 py-3 text-sm font-bold text-white hover:bg-teal-700 transition-colors">
        تواصل للترقية الآن
      </button>
      <button onClick={onLogout} data-testid="trial-expired-logout-btn" className="rounded-xl border border-slate-300 px-6 py-3 text-sm font-bold text-slate-600 hover:bg-slate-50 transition-colors">
        تسجيل الخروج
      </button>
    </div>
  </div>
);

export default function OrganizationLayout({ children, club, user, trialStatus, seasons, selectedSeasonId, setSelectedSeasonId }) {
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('orgSidebarCollapsed') === '1');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const menuRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const orgType = getOrganizationType(club);
  const logoUrl = club?.logo_file_id ? `${API}/files/${club.logo_file_id}` : null;
  const pageTitle = getRouteTitle(location.pathname);
  const displayName = user?.full_name || user?.username || '';
  const initial = displayName ? displayName.trim().charAt(0) : '';

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      localStorage.setItem('orgSidebarCollapsed', c ? '0' : '1');
      return !c;
    });
  };

  useEffect(() => {
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setUserMenuOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    // إعادة تحميل كاملة لإعادة تهيئة حالة المصادقة في AuthProvider
    window.location.replace('/login');
  };

  return (
    <div dir="rtl" className="min-h-screen bg-[#f4f6fa]" style={{ fontFamily: "'Noto Sans Arabic', Arial, sans-serif" }}>
      <aside
        className={`fixed inset-y-0 right-0 z-40 hidden lg:block bg-[#101b2e] transition-all duration-200 ${collapsed ? 'w-[72px]' : 'w-60'}`}
        data-testid="org-sidebar"
      >
        <SidebarContent collapsed={collapsed} club={club} orgType={orgType} user={user} currentPath={location.pathname} logoUrl={logoUrl} />
        <button
          onClick={toggleCollapsed}
          data-testid="sidebar-collapse-btn"
          className="absolute -left-3 top-9 z-10 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md border border-white/10 bg-[#101b2e] text-slate-300 shadow-md hover:bg-[#1e2f4e] hover:text-white transition-colors"
          title={collapsed ? 'توسيع القائمة' : 'طي القائمة'}
        >
          <Icon name="chevronLeft" className={`w-3.5 h-3.5 transition-transform ${collapsed ? '' : 'rotate-180'}`} />
        </button>
      </aside>

      {drawerOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDrawerOpen(false)} />
          <aside className="absolute inset-y-0 right-0 w-72 max-w-[85%] bg-[#101b2e] shadow-2xl">
            <button onClick={() => setDrawerOpen(false)} data-testid="drawer-close-btn" className="absolute top-4 left-4 z-10 text-slate-400 hover:text-white">
              <Icon name="x" className="w-5 h-5" />
            </button>
            <SidebarContent collapsed={false} club={club} orgType={orgType} user={user} currentPath={location.pathname} logoUrl={logoUrl} onNavigate={() => setDrawerOpen(false)} />
          </aside>
        </div>
      )}

      <div className={`transition-all duration-200 ${collapsed ? 'lg:mr-[72px]' : 'lg:mr-60'}`}>
        <header className="sticky top-0 z-30 h-16 w-full border-b border-slate-200/80 bg-white/90 backdrop-blur-md shadow-sm" data-testid="org-topbar">
          <div className="flex h-full items-center gap-3 px-4 sm:px-6 lg:px-8">
            <button onClick={() => setDrawerOpen(true)} className="lg:hidden rounded-xl p-2 -mr-1.5 text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors" data-testid="topbar-menu-btn">
              <Icon name="menu" className="w-6 h-6" />
            </button>
            <div className="flex items-center gap-2.5 min-w-0">
              <h1 className="text-base sm:text-lg font-black text-slate-900 tracking-tight truncate" data-testid="topbar-page-title">{pageTitle}</h1>
            </div>

            <div className="mr-auto flex items-center gap-2 sm:gap-3">
              <SeasonSwitcher seasons={seasons} selectedSeasonId={selectedSeasonId} setSelectedSeasonId={setSelectedSeasonId} />
              <GlobalSearchTrigger user={user} />
              <span className="hidden items-center gap-1.5 rounded-full border border-orange-200 bg-orange-50 px-2.5 py-1 text-[11px] font-bold text-orange-600 md:inline-flex" data-testid="branches-coming-soon-badge">
                <Icon name="clock" className="h-3 w-3" /> الفروع قريباً
              </span>
              <HeaderHelpMenu onRaiseTicket={() => navigate('/support')} className="hidden sm:flex" />
              <button
                onClick={() => navigate('/support')}
                data-testid="topbar-raise-ticket-btn"
                className="hidden sm:inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-teal-600 to-teal-700 px-3.5 py-2 text-xs font-extrabold text-white shadow-sm border border-teal-500/30 transition-all hover:from-teal-700 hover:to-teal-800 hover:shadow-md hover:shadow-teal-500/20 active:scale-95"
              >
                <Icon name="plus" className="w-4 h-4" /> فتح مذكرة دعم
              </button>

              <NotificationBell user={user} align="left" dropdownDirection="down" />
              <div className="relative" ref={menuRef}>
                <button onClick={() => setUserMenuOpen(!userMenuOpen)} data-testid="topbar-user-menu-btn" className="group flex items-center gap-2.5 rounded-2xl border border-slate-200/90 bg-white p-1.5 pl-3 shadow-[0_1px_2px_rgba(0,0,0,0.03)] transition-all hover:border-slate-300 hover:bg-slate-50">
                  <div className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#101b2e] to-[#1a2d4a] text-sm font-black text-white shadow-sm transition-transform group-hover:scale-105">
                    {initial}
                    <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-white" />
                  </div>
                  <div className="hidden sm:block text-right">
                    <div className="text-sm font-bold text-slate-900 leading-tight" data-testid="topbar-user-name">{displayName}</div>
                    <span className="inline-flex items-center rounded-full bg-teal-50 border border-teal-200/60 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-teal-800">{getRoleLabel(user?.role)}</span>
                  </div>
                  <Icon name="chevronDown" className="hidden sm:block w-3.5 h-3.5 text-slate-400" />
                </button>
                {userMenuOpen && (
                  <div className="absolute left-0 mt-2 w-60 rounded-2xl border border-slate-200/80 bg-white p-2 shadow-2xl z-50" data-testid="user-menu-dropdown">
                    <div className="px-3 py-2 border-b border-slate-100 mb-1">
                      <div className="text-sm font-bold text-slate-900 truncate">{displayName}</div>
                      <div className="text-xs text-slate-400 truncate">{club?.name || club?.club_name}</div>
                    </div>
                    <div className="sm:hidden">
                      <Link to="/help" onClick={() => setUserMenuOpen(false)} data-testid="user-menu-help-center" className="flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-semibold text-slate-700 hover:bg-teal-50 hover:text-teal-800 transition-colors">
                        <Icon name="info" className="w-4 h-4 text-teal-500" /> المساعدة والأسئلة الشائعة
                      </Link>
                      <button onClick={() => { setUserMenuOpen(false); navigate('/support'); }} data-testid="user-menu-raise-ticket" className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-semibold text-slate-700 hover:bg-teal-50 hover:text-teal-800 transition-colors">
                        <Icon name="inbox" className="w-4 h-4 text-teal-500" /> تواصل مع الدعم الفني
                      </button>
                      <div className="my-1 border-t border-slate-100" />
                    </div>
                    <Link to="/settings" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-semibold text-slate-700 hover:bg-teal-50 hover:text-teal-800 transition-colors">
                      <Icon name="cog" className="w-4 h-4 text-teal-500" /> الإعدادات
                    </Link>
                    <button onClick={handleLogout} data-testid="logout-btn" className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors">
                      <Icon name="logout" className="w-4 h-4" /> تسجيل الخروج
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        <TrialBanner status={trialStatus} />

        <main className="p-4 sm:p-6" data-testid="org-main-content">
          {trialStatus?.applicable && trialStatus.is_expired
            ? <TrialExpiredScreen onLogout={handleLogout} />
            : children}
        </main>
      </div>
    </div>
  );
}
